import type { MindItem } from '@kimuno/api-sdk/lib/types/responses/MindItem';
import { noop } from 'lodash';
import type { Instance } from 'pspdfkit';
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { fetchMindItem as fetchMindItemAction } from '../../actions/mindItems.actions';
import { READER_ZOOM_FACTOR } from '../../constants';
import { selectIsItemInOwnCollections } from '../../reducers/collections.selectors';
import { selectAdjacentSiblings, selectMindItem } from '../../reducers/mindItems.selectors';
import { notifyBugsnagHandledError } from '../../utils/bugsnagClient';
import useAction from '../../utils/useAction';
import useRouter from '../../utils/useRouter';
import useScreenSize from '../../utils/useScreenSize';
import type { Alert } from '../NotificationAlert/NotificationAlert';

interface ReaderContext {
	mindItemId: string;
	isReadOnly: boolean;
	previousSibling?: MindItem;
	nextSibling?: MindItem;
	isPinned: boolean;
	isInCollection: boolean;
	isTextBased: boolean;
	onPageLoad: () => void;
	pagesLoaded: boolean;
	currentPage: number;
	setCurrentPage: (page: number) => void;
	initialPage: number;
	setInitialPage: (page: number) => void;
	hasAttachments: boolean;
	isReaderDetailsCollapsed?: boolean;
	setReaderDetailsCollapsed?: (isVisible: boolean) => void;
	pspdfkitInstance?: Instance;
	setPspdfkitInstance?: (instance: Instance | undefined) => void;
	showNewReader?: boolean;
	zoomProperties: {
		zoomFactor: number;
		setZoomFactor: (factor: number) => void;
		baselineZoomFactor: number;
		setBaselineZoomFactor: (factor: number) => void;
	};
	fullScreenMode?: boolean;
	setFullScreenMode?: (mode: boolean) => void;
	alert?: Alert;
	setAlert?: (alert: Alert | undefined) => void;
	selectedPagesForAI: number[];
	setSelectedPagesForAI: (pages: number[]) => void;
}

export const ReaderContext = createContext<ReaderContext>({
	mindItemId: '',
	isReadOnly: false,
	previousSibling: undefined,
	nextSibling: undefined,
	currentPage: 0,
	setCurrentPage: noop,
	isPinned: false,
	isInCollection: false,
	isTextBased: false,
	onPageLoad: noop,
	pagesLoaded: false,
	initialPage: 1,
	setInitialPage: noop,
	hasAttachments: false,
	isReaderDetailsCollapsed: false,
	setReaderDetailsCollapsed: noop,
	pspdfkitInstance: undefined,
	setPspdfkitInstance: noop,
	showNewReader: true,
	zoomProperties: {
		zoomFactor: READER_ZOOM_FACTOR.OneHundred,
		setZoomFactor: noop,
		baselineZoomFactor: READER_ZOOM_FACTOR.OneHundred,
		setBaselineZoomFactor: noop,
	},
	fullScreenMode: false,
	setFullScreenMode: noop,
	alert: undefined,
	setAlert: noop,
	selectedPagesForAI: [],
	setSelectedPagesForAI: noop,
});

const ReaderContextProvider: React.FC = (props) => {
	const { isMdScreen, isLgScreen } = useScreenSize();
	const isMdUpScreen = isMdScreen || isLgScreen;
	const [zoomFactor, setZoomFactor] = useState(READER_ZOOM_FACTOR.OneHundred);
	const [baselineZoomFactor, setBaselineZoomFactor] = useState(READER_ZOOM_FACTOR.OneHundred);
	const prevIsMdScreenRef = useRef<boolean>(isMdScreen);

	const [alert, setAlert] = useState<Alert | undefined>();
	const [currentPage, setCurrentPage] = useState(1);
	const [initialPage, setInitialPage] = useState(1);
	const [fullScreenMode, setFullScreenMode] = useState(false);
	const [loadedPages, setLoadedPages] = useState<number>(0);
	const [isReaderDetailsCollapsed, setReaderDetailsCollapsed] = useState<boolean>(isMdScreen);
	const [pspdfkitInstance, setPspdfkitInstance] = useState<Instance | undefined>(undefined);
	const [selectedPagesForAI, setSelectedPagesForAI] = useState<number[]>([]);

	const fetchMindItem = useAction(fetchMindItemAction);
	const { match } = useRouter();
	const documentId = (match.params as { documentId?: string })?.documentId;

	const mindItem = useSelector((state) => (documentId ? selectMindItem(state, documentId) : null));
	const isInCollection = useSelector((state) =>
		documentId ? selectIsItemInOwnCollections(state, documentId) : false
	);

	const adjacentSiblings = useSelector((state) =>
		documentId ? selectAdjacentSiblings(state, { id: documentId, type: 'mindItem' }) : null
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome hook deps kept as-is; changing the deps list alters effect timing
	useEffect(() => {
		setLoadedPages(0);
		setSelectedPagesForAI([]);
		setZoomFactor(READER_ZOOM_FACTOR.OneHundred);
		setBaselineZoomFactor(READER_ZOOM_FACTOR.OneHundred);

		if (pspdfkitInstance) {
			try {
				window.PSPDFKit.unload(pspdfkitInstance);
				setPspdfkitInstance(undefined);
			} catch (error) {
				notifyBugsnagHandledError(error as Error);
			}
		}

		if (documentId) {
			fetchMindItem(documentId);
		} else {
			setCurrentPage(1);
		}
	}, [documentId]);

	const nextSibling = adjacentSiblings?.next?.mindItem;
	const previousSibling = adjacentSiblings?.previous?.mindItem;

	const handlePageLoad = () => {
		setLoadedPages(loadedPages + 1);
	};

	useEffect(() => {
		if (prevIsMdScreenRef.current !== isMdScreen) {
			setReaderDetailsCollapsed(isMdScreen);
			prevIsMdScreenRef.current = isMdScreen;
		}
	}, [isMdScreen]);

	const contextValue: ReaderContext = {
		currentPage: currentPage,
		setCurrentPage: setCurrentPage,
		initialPage: initialPage,
		setInitialPage: setInitialPage,
		mindItemId: mindItem?.id ?? '',
		isReadOnly: mindItem?.isReadOnly ?? false,
		previousSibling: mindItem ? previousSibling : undefined,
		nextSibling: mindItem ? nextSibling : undefined,
		isPinned: mindItem?.isPinned ?? false,
		isInCollection: mindItem ? isInCollection : false,
		isTextBased: mindItem?.isTextBased ?? false,
		onPageLoad: handlePageLoad,
		pagesLoaded: Boolean(
			mindItem?.fileType === 'document' && loadedPages >= (mindItem?.pages?.length ?? 0)
		),
		hasAttachments: mindItem?.hasAttachments ?? false,
		isReaderDetailsCollapsed,
		setReaderDetailsCollapsed,
		showNewReader: true,
		pspdfkitInstance,
		setPspdfkitInstance,
		zoomProperties: {
			zoomFactor: zoomFactor,
			setZoomFactor: setZoomFactor,
			baselineZoomFactor: baselineZoomFactor,
			setBaselineZoomFactor: setBaselineZoomFactor,
		},
		fullScreenMode: fullScreenMode && isMdUpScreen,
		setFullScreenMode,
		alert,
		setAlert,
		selectedPagesForAI,
		setSelectedPagesForAI,
	};

	return <ReaderContext.Provider value={contextValue}>{props.children}</ReaderContext.Provider>;
};

export const useReaderContext = () => useContext(ReaderContext);

export default ReaderContextProvider;
